import { useState, useEffect, useMemo } from 'react';
import { devicesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Cpu, Link2, CheckCircle2, Loader2, Building2, MapPin, Wifi, Radio,
  AlertCircle
} from 'lucide-react';

export default function SensorLinkingPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [euiInputs, setEuiInputs] = useState({});
  const [connTypes, setConnTypes] = useState({});
  const [linking, setLinking] = useState(null);

  useEffect(() => { loadPending(); }, []);

  const loadPending = async () => {
    try {
      const res = await devicesApi.pending();
      setDevices(res.data.devices || []);
    } catch {
      toast.error('Kunde inte ladda enheter');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (device) => {
    const eui = (euiInputs[device.id] || '').trim();
    if (!eui) {
      toast.error('Ange sensor-ID (EUI)');
      return;
    }
    setLinking(device.id);
    try {
      const res = await devicesApi.link(device.id, {
        device_eui: eui,
        connection_type: connTypes[device.id] || 'wifi',
      });
      toast.success(res.data.message);
      setEuiInputs(prev => { const n = { ...prev }; delete n[device.id]; return n; });
      loadPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte koppla sensor');
    } finally {
      setLinking(null);
    }
  };

  // Group by school
  const grouped = useMemo(() => {
    const map = {};
    for (const d of devices) {
      const key = d.school_name || 'Okänd skola';
      if (!map[key]) map[key] = [];
      map[key].push(d);
    }
    return Object.entries(map);
  }, [devices]);

  const totalPending = devices.length;

  if (loading) {
    return <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="sensor-linking-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="h-5 w-5 text-slate-400" />
          <span className="text-xs font-ibm uppercase tracking-widest text-slate-400">Sensorkoppling</span>
        </div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
          Koppla fysiska sensorer
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm text-sm max-w-2xl">
          Ange sensor-ID (EUI) för varje förberedd plats. Sensorn hittas på enhetens baksida eller i tillverkarens app.
        </p>
      </div>

      {totalPending === 0 ? (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-chivo font-bold text-lg text-navy dark:text-white">Alla sensorer kopplade</h2>
            <p className="text-slate-500 dark:text-slate-400 font-ibm text-sm mt-1">
              Det finns inga väntande sensorplatser. Nya platser skapas via kundregistreringen.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-ibm">
              <AlertCircle className="h-3 w-3 mr-1" />
              {totalPending} {totalPending === 1 ? 'sensor' : 'sensorer'} väntar på koppling
            </Badge>
          </div>

          {grouped.map(([schoolName, schoolDevices]) => (
            <div key={schoolName} className="space-y-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                <h2 className="font-chivo font-bold text-lg text-navy dark:text-white">{schoolName}</h2>
                <span className="text-xs font-ibm text-slate-400">{schoolDevices.length} st</span>
              </div>

              {schoolDevices.map((device, idx) => (
                <Card key={device.id} className="border-slate-200 dark:border-slate-700" data-testid={`pending-device-${device.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Device info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
                          <Cpu className="h-5 w-5 text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-ibm font-semibold text-sm text-navy dark:text-white truncate">
                            {device.name || device.room_name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {device.department && (
                              <span className="text-[10px] font-ibm text-slate-400 flex items-center gap-0.5">
                                <MapPin className="h-2.5 w-2.5" /> {device.department}
                              </span>
                            )}
                            <span className="text-[10px] font-ibm text-slate-300">#{idx + 1}</span>
                          </div>
                        </div>
                      </div>

                      {/* Link form */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1">
                        <Input
                          value={euiInputs[device.id] || ''}
                          onChange={e => setEuiInputs(prev => ({ ...prev, [device.id]: e.target.value }))}
                          placeholder="Sensor-ID (EUI)"
                          className="h-11 sm:h-9 text-base sm:text-sm font-mono flex-1"
                          data-testid={`eui-input-${device.id}`}
                        />
                        <select
                          value={connTypes[device.id] || 'wifi'}
                          onChange={e => setConnTypes(prev => ({ ...prev, [device.id]: e.target.value }))}
                          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-ibm h-11 sm:h-9 min-w-[100px]"
                          data-testid={`conn-type-${device.id}`}
                        >
                          <option value="wifi">WiFi</option>
                          <option value="lorawan">LoRaWAN</option>
                        </select>
                        <Button
                          onClick={() => handleLink(device)}
                          disabled={linking === device.id || !euiInputs[device.id]?.trim()}
                          className="gap-1.5 h-11 sm:h-9 shrink-0"
                          data-testid={`link-btn-${device.id}`}
                        >
                          {linking === device.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2 className="h-4 w-4" />
                          )}
                          Koppla
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
